import { send, validateSerial } from '../../common/utils';
import { HUB_SCENES_CREATE } from '../../common/methods';

/**
 * Creates a new scene/rule on the specified hub via WebSocket API.
 * This is the low-level API function that validates the serial and sends the WebSocket message.
 *
 * @param {string} serial - Hub serial number (e.g., "90004631")
 * @param {Object} params - Scene/rule parameters object
 * @param {boolean} params.enabled - Whether the rule is enabled
 * @param {string} params.name - Display name for the rule
 * @param {Array} params.when - Array of trigger blocks
 * @param {Array} params.then - Array of action blocks
 * @param {Array} params.else - Array of else action blocks
 * @param {Array} params.exceptions - Array of exception blocks
 * @param {string} [params.exec_policy] - Execution policy ('wait_result', 'ignore_result', 'check_result')
 * @param {Array<string>} params.house_modes - Array of house mode IDs
 * @param {Object} [params.meta] - Additional metadata
 * @param {Function} onSuccess - Callback function called when the API request succeeds
 * @param {Function} onError - Callback function called when the API request fails
 *
 * @returns {*} Returns the result of the send function (usually the WebSocket message)
 *
 * @throws {Error} Throws validation error if serial is invalid
 *
 * @example
 * const rule = {
 *   enabled: true,
 *   name: "Motion Alert",
 *   when: [{ blockType: "when", ... }],
 *   then: [{ blockType: "then", ... }],
 *   house_modes: ["1", "2"]
 * };
 *
 * hubScenesCreateApi(
 *   "90004631",
 *   rule,
 *   (response) => console.log('Success:', response),
 *   (error) => console.error('Error:', error)
 * );
 */
export const hubScenesCreateApi = (serial, params, onSuccess, onError) => {
    validateSerial(serial);

    return send(serial, HUB_SCENES_CREATE, params, onSuccess, onError);
};

/**
 * Alias for hubScenesCreateApi function.
 * Creates a new scene/rule on the specified hub via WebSocket API.
 *
 * @param {string} serial - Hub serial number
 * @param {Object} params - Scene/rule parameters object (see hubScenesCreateApi for structure)
 * @param {Function} onSuccess - Success callback function
 * @param {Function} onError - Error callback function
 * @returns {*} Returns the result of hubScenesCreateApi
 * @see {[hub.scenes.create](https://log.ezlo.com/public/hub/scenes/local_scenes_api/#hubscenescreate)}
 *
 * @example
 * create("90004631", ruleParams, onSuccess, onError);
 */
export const create = (serial, params, onSuccess, onError) => hubScenesCreateApi(serial, params, onSuccess, onError);
